import { Component, Inject, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
    AtokuCategory,
    AtokuCategoryUpsertRequest,
    AtokuSvgLibraryItem,
    AtokuSvgLibrarySearchResponse
} from '../../../models/atoku-category.model';
import { AtokuService } from '../../../services/atoku.service';
import { Subject, catchError, debounceTime, distinctUntilChanged, finalize, of, startWith, switchMap, takeUntil, tap } from 'rxjs';

export interface EditCategoryDialogData {
    mode: 'add' | 'edit';
    category: AtokuCategory;
    storageCategoryExists?: boolean;
}

@Component({
    selector: 'app-edit-category-dialog',
    templateUrl: './edit-category-dialog.component.html',
    styleUrls: ['./edit-category-dialog.component.scss']
})
export class EditCategoryDialogComponent implements OnDestroy {
    readonly defaultThemeColor = '#2500fe';
    form: FormGroup;
    svgSearchControl = new FormControl('', { nonNullable: true });
    logoPreviewDataUrl = '';
    svgColorValue = this.defaultThemeColor;
    svgLibraryErrorMessage = '';
    svgLibraryItems: AtokuSvgLibraryItem[] = [];
    selectedSvgLibrary: AtokuSvgLibraryItem | null = null;
    isLoadingSvgLibrary = false;
    private svgBaseLogo = '';
    private svgBaseColor = this.defaultThemeColor;
    private svgColorCustomized = false;
    private readonly destroy$ = new Subject<void>();

    constructor(
        public dialogRef: MatDialogRef<EditCategoryDialogComponent, AtokuCategoryUpsertRequest>,
        @Inject(MAT_DIALOG_DATA) public data: EditCategoryDialogData,
        private fb: FormBuilder,
        private atokuService: AtokuService
    ) {
        this.selectedSvgLibrary = data.category.svgLibraryId
            ? {
                id: data.category.svgLibraryId,
                code: data.category.svgLibraryCode ?? '',
                name: data.category.svgLibraryName ?? 'Attached SVG',
                description: '',
                groupName: '',
                tags: '',
                svg: data.category.logo,
                isActive: true,
                createdOn: ''
            }
            : null;
        this.logoPreviewDataUrl = this.getSvgDataUrl(data.category.logo);
        this.form = this.fb.group({
            code: [data.category.code, [Validators.required, Validators.maxLength(16)]],
            displayName: [data.category.displayName, [Validators.required, Validators.maxLength(120)]],
            description: [data.category.description, [Validators.required, Validators.maxLength(300)]],
            theme: [this.normalizeThemeColor(data.category.theme), [Validators.required, Validators.pattern(/^#[0-9a-fA-F]{6}$/)]],
            logo: [data.category.logo, [Validators.required]],
            svgLibraryId: [data.category.svgLibraryId ?? null],
            svgColor: [this.defaultThemeColor, [Validators.required, Validators.pattern(/^#[0-9a-fA-F]{6}$/)]],
            isStorage: [false]
        });
        this.syncSvgEditorState(data.category.logo);

        const isStorageControl = this.form.get('isStorage');
        const codeControl = this.form.get('code');
        if (this.data.mode === 'add' && this.data.storageCategoryExists) {
            isStorageControl?.disable({ emitEvent: false });
        }

        isStorageControl?.valueChanges.subscribe((isStorage: boolean) => {
            if (isStorage) {
                codeControl?.setValue('STOR', { emitEvent: false });
                codeControl?.disable({ emitEvent: false });
                return;
            }

            if (this.data.mode === 'add') {
                codeControl?.enable({ emitEvent: false });
            }
        });

        this.svgSearchControl.valueChanges.pipe(
            startWith(''),
            debounceTime(250),
            distinctUntilChanged(),
            tap(() => {
                this.isLoadingSvgLibrary = true;
                this.svgLibraryErrorMessage = '';
            }),
            switchMap(query => this.atokuService.searchSvgLibrary({
                query: query.trim() || undefined,
                isActive: true,
                page: 1,
                pageSize: 24
            }).pipe(
                catchError(() => {
                    this.svgLibraryErrorMessage = 'Could not load the SVG library.';
                    return of({
                        items: [],
                        total: 0,
                        page: 1,
                        pageSize: 24
                    } as AtokuSvgLibrarySearchResponse);
                }),
                finalize(() => {
                    this.isLoadingSvgLibrary = false;
                })
            )),
            takeUntil(this.destroy$)
        ).subscribe(response => {
            this.svgLibraryItems = this.includeSelectedSvg(response.items);
        });
    }

    get title(): string {
        return this.data.mode === 'add' ? 'Add Category' : 'Edit Category';
    }

    get isStorageDisabled(): boolean {
        return this.data.mode !== 'add' || !!this.data.storageCategoryExists;
    }

    get hasSvgSelection(): boolean {
        return !!this.logoPreviewDataUrl;
    }

    get themeColorValue(): string {
        return this.normalizeThemeColor(this.form.get('theme')?.value);
    }

    get canEditSvgColor(): boolean {
        return !!this.svgBaseLogo;
    }

    selectSvgLibrary(item: AtokuSvgLibraryItem): void {
        this.selectedSvgLibrary = item;
        this.form.patchValue({
            logo: item.svg,
            svgLibraryId: item.id
        });
        this.syncSvgEditorState(item.svg, true);
        this.form.get('svgLibraryId')?.markAsDirty();
    }

    onThemeColorChange(color: string): void {
        const normalizedColor = this.normalizeThemeColor(color);
        this.form.patchValue({
            theme: normalizedColor
        });
        this.form.get('theme')?.markAsDirty();
        this.form.get('theme')?.markAsTouched();
    }

    onThemeTextInput(value: string): void {
        this.form.patchValue({
            theme: value
        });
        this.form.get('theme')?.markAsDirty();
    }

    onThemeTextBlur(value: string): void {
        const normalizedValue = this.normalizeThemeColor(value, false);
        if (normalizedValue) {
            this.form.patchValue({
                theme: normalizedValue
            });
        }
        this.form.get('theme')?.markAsTouched();
    }

    onSvgColorChange(color: string): void {
        const normalizedColor = this.normalizeThemeColor(color);
        this.svgColorValue = normalizedColor;
        this.form.patchValue({
            svgColor: normalizedColor
        });
        this.form.get('svgColor')?.markAsDirty();
        this.form.get('svgColor')?.markAsTouched();
        this.applySvgColor(normalizedColor);
    }

    onSvgColorTextInput(value: string): void {
        this.form.patchValue({
            svgColor: value
        });
        this.form.get('svgColor')?.markAsDirty();

        const normalizedColor = this.normalizeThemeColor(value, false);
        if (/^#[0-9a-fA-F]{6}$/.test(normalizedColor)) {
            this.svgColorValue = normalizedColor.toLowerCase();
            this.applySvgColor(this.svgColorValue);
        }
    }

    onSvgColorTextBlur(value: string): void {
        const normalizedColor = this.normalizeThemeColor(value, false);
        if (/^#[0-9a-fA-F]{6}$/.test(normalizedColor)) {
            this.svgColorValue = normalizedColor.toLowerCase();
            this.form.patchValue({
                svgColor: this.svgColorValue
            });
            this.applySvgColor(this.svgColorValue);
        }
        this.form.get('svgColor')?.markAsTouched();
    }

    private applySvgColor(normalizedColor: string): void {
        if (!this.svgBaseLogo) {
            return;
        }

        if (normalizedColor === this.svgBaseColor) {
            this.svgColorCustomized = false;
            this.setRenderedLogo(this.svgBaseLogo, true);
            return;
        }

        const recoloredSvg = this.recolorSvg(this.svgBaseLogo, normalizedColor);
        this.svgColorCustomized = recoloredSvg.trim() !== this.svgBaseLogo.trim();
        this.setRenderedLogo(recoloredSvg, true);
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    onSave(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        const value = this.form.getRawValue();
        this.dialogRef.close({
            code: String(value.code).trim().toUpperCase(),
            displayName: String(value.displayName).trim(),
            description: String(value.description).trim(),
            logo: String(value.logo ?? ''),
            theme: this.normalizeThemeColor(value.theme),
            svgLibraryId: this.svgColorCustomized ? null : value.svgLibraryId ? String(value.svgLibraryId) : null
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    getSvgDataUrl(svg: string): string {
        return svg ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}` : '';
    }

    private includeSelectedSvg(items: AtokuSvgLibraryItem[]): AtokuSvgLibraryItem[] {
        if (!this.selectedSvgLibrary) {
            return items;
        }

        return items.some(item => item.id === this.selectedSvgLibrary?.id)
            ? items
            : [this.selectedSvgLibrary, ...items];
    }

    private syncSvgEditorState(svg: string, markDirty = false): void {
        const normalizedSvg = String(svg ?? '').trim();
        this.svgBaseLogo = normalizedSvg;
        this.svgBaseColor = this.extractSvgColor(normalizedSvg) ?? this.defaultThemeColor;
        this.svgColorValue = this.svgBaseColor;
        this.form.patchValue({
            svgColor: this.svgColorValue
        });
        this.svgColorCustomized = false;
        this.setRenderedLogo(normalizedSvg, markDirty);
    }

    private setRenderedLogo(svg: string, markDirty = false): void {
        const normalizedSvg = String(svg ?? '').trim();
        this.form.patchValue({
            logo: normalizedSvg
        });
        this.logoPreviewDataUrl = this.getSvgDataUrl(normalizedSvg);

        if (markDirty) {
            this.form.get('logo')?.markAsDirty();
            this.form.get('logo')?.markAsTouched();
        }
    }

    private extractSvgColor(svg: string): string | null {
        const document = this.parseSvg(svg);
        if (!document) {
            return null;
        }

        const elements = [document.documentElement, ...Array.from(document.documentElement.querySelectorAll('*'))];
        for (const element of elements) {
            const attributeColor = this.normalizeSvgPaintValue(element.getAttribute('fill'))
                ?? this.normalizeSvgPaintValue(element.getAttribute('stroke'))
                ?? this.normalizeSvgPaintValue(element.getAttribute('color'));
            if (attributeColor) {
                return attributeColor;
            }

            const styleColor = this.extractSvgStyleColor(element.getAttribute('style'));
            if (styleColor) {
                return styleColor;
            }
        }

        return null;
    }

    private extractSvgStyleColor(style: string | null): string | null {
        if (!style) {
            return null;
        }

        for (const declaration of style.split(';')) {
            const [property, ...valueParts] = declaration.split(':');
            if (!property || valueParts.length === 0) {
                continue;
            }

            const normalizedProperty = property.trim().toLowerCase();
            if (!['fill', 'stroke', 'color'].includes(normalizedProperty)) {
                continue;
            }

            const normalizedColor = this.normalizeSvgPaintValue(valueParts.join(':'));
            if (normalizedColor) {
                return normalizedColor;
            }
        }

        return null;
    }

    private normalizeSvgPaintValue(value: string | null | undefined): string | null {
        const trimmedValue = String(value ?? '').trim();
        if (!trimmedValue || trimmedValue.toLowerCase() === 'none' || trimmedValue.toLowerCase().startsWith('url(')) {
            return null;
        }

        if (/^#[0-9a-fA-F]{6}$/.test(trimmedValue) || /^#[0-9a-fA-F]{3}$/.test(trimmedValue)) {
            return this.normalizeThemeColor(trimmedValue);
        }

        if (typeof document === 'undefined') {
            return null;
        }

        const colorProbe = document.createElement('span');
        colorProbe.style.color = '';
        colorProbe.style.color = trimmedValue;
        const resolvedColor = colorProbe.style.color;
        const rgbMatch = resolvedColor.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
        if (!rgbMatch) {
            return null;
        }

        return `#${[1, 2, 3]
            .map(index => Number(rgbMatch[index]).toString(16).padStart(2, '0'))
            .join('')}`;
    }

    private recolorSvg(svg: string, color: string): string {
        const document = this.parseSvg(svg);
        if (!document) {
            return svg;
        }

        const root = document.documentElement;
        root.setAttribute('color', color);

        let updatedAnyPaint = false;
        const elements = [root, ...Array.from(root.querySelectorAll('*'))];
        for (const element of elements) {
            if (element !== root && this.shouldSkipSvgElement(element)) {
                continue;
            }

            updatedAnyPaint = this.applySvgColorToElement(element, color) || updatedAnyPaint;
        }

        if (!updatedAnyPaint) {
            root.setAttribute('fill', color);
        }

        return new XMLSerializer().serializeToString(root);
    }

    private shouldSkipSvgElement(element: Element): boolean {
        return !!element.closest('defs, clipPath, mask, filter, linearGradient, radialGradient, pattern, marker, symbol');
    }

    private applySvgColorToElement(element: Element, color: string): boolean {
        let updated = false;

        for (const attribute of ['fill', 'stroke']) {
            const currentValue = element.getAttribute(attribute);
            if (currentValue && currentValue.toLowerCase() !== 'none' && !currentValue.toLowerCase().startsWith('url(')) {
                element.setAttribute(attribute, color);
                updated = true;
            }
        }

        const style = element.getAttribute('style');
        const nextStyle = this.replaceSvgPaintDeclarations(style, color);
        if (nextStyle !== style) {
            if (nextStyle) {
                element.setAttribute('style', nextStyle);
            } else {
                element.removeAttribute('style');
            }
            updated = true;
        }

        if (element.hasAttribute('color')) {
            element.setAttribute('color', color);
            updated = true;
        }

        return updated;
    }

    private replaceSvgPaintDeclarations(style: string | null, color: string): string | null {
        if (!style) {
            return style;
        }

        let updated = false;
        const nextDeclarations = style
            .split(';')
            .map(declaration => declaration.trim())
            .filter(Boolean)
            .map(declaration => {
                const [property, ...valueParts] = declaration.split(':');
                if (!property || valueParts.length === 0) {
                    return declaration;
                }

                const normalizedProperty = property.trim().toLowerCase();
                const value = valueParts.join(':').trim();
                if (!['fill', 'stroke', 'color'].includes(normalizedProperty)) {
                    return declaration;
                }

                if (normalizedProperty !== 'color' && (!value || value.toLowerCase() === 'none' || value.toLowerCase().startsWith('url('))) {
                    return declaration;
                }

                updated = true;
                return `${property.trim()}: ${color}`;
            });

        return updated ? nextDeclarations.join('; ') : style;
    }

    private parseSvg(svg: string): XMLDocument | null {
        const normalizedSvg = String(svg ?? '').trim();
        if (!normalizedSvg) {
            return null;
        }

        const document = new DOMParser().parseFromString(normalizedSvg, 'image/svg+xml');
        return document.documentElement?.nodeName === 'parsererror' ? null : document;
    }

    private normalizeThemeColor(value: unknown, fallbackToDefault = true): string {
        const trimmedValue = String(value ?? '').trim();
        if (/^#[0-9a-fA-F]{6}$/.test(trimmedValue)) {
            return trimmedValue.toLowerCase();
        }

        if (/^#[0-9a-fA-F]{3}$/.test(trimmedValue)) {
            return `#${trimmedValue
                .slice(1)
                .split('')
                .map(character => `${character}${character}`)
                .join('')}`.toLowerCase();
        }

        return fallbackToDefault ? this.defaultThemeColor : trimmedValue;
    }
}
